import { Head, Link, useForm, usePage } from '@inertiajs/react'
import { Anchor, Badge, Button, Paper, Stack, Text, TextInput, Textarea, Title } from '@mantine/core'
import { type FormEvent, useState } from 'react'
import AssignSkillForm from '@/components/AssignSkillForm'
import EmptyState from '@/components/EmptyState'
import PageLayout from '@/components/PageLayout'
import SkillAssignmentRow from '@/components/SkillAssignmentRow'
import type { SkillIndexPageProps, SkillProps } from '@/types'

function SkillCard({ skill }: { skill: SkillProps }) {
  return (
    <Paper p="md" withBorder>
      <Stack gap={6}>
        <div>
          <Anchor component={Link} href={`/skills/${skill.id}`} fw={600}>
            {skill.name}
          </Anchor>
          <Text c="dimmed" size="sm">{skill.slug}</Text>
        </div>
        {skill.system ? <Badge color="blue" variant="light">System</Badge> : null}
        <Text size="sm">{skill.description}</Text>
        {skill.tools.length > 0 ? (
          <Text c="dimmed" size="sm">Tools: {skill.tools.join(', ')}</Text>
        ) : null}
      </Stack>
    </Paper>
  )
}

function NewSkillForm() {
  const { csrf_token } = usePage<SkillIndexPageProps>().props
  const [open, setOpen] = useState(false)

  const { data, setData, post, processing, reset } = useForm({
    skill: { name: '', description: '', body: '' },
    authenticity_token: csrf_token || '',
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post(`/skills`, {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        setOpen(false)
      },
    })
  }

  if (!open) {
    return (
      <Button variant="light" onClick={() => setOpen(true)}>
        + New Skill
      </Button>
    )
  }

  return (
    <Paper component="form" onSubmit={handleSubmit} p="lg">
      <Stack gap="md">
        <TextInput
          id="skill-name"
          label="Name"
          value={data.skill.name}
          onChange={(e) => setData('skill', { ...data.skill, name: e.target.value })}
        />

        <TextInput
          id="skill-desc"
          label="Description"
          value={data.skill.description}
          onChange={(e) => setData('skill', { ...data.skill, description: e.target.value })}
        />

        <Textarea
          id="skill-body"
          label="Instructions"
          autosize
          minRows={6}
          value={data.skill.body}
          onChange={(e) => setData('skill', { ...data.skill, body: e.target.value })}
        />

        <Button.Group>
          <Button type="submit" loading={processing}>Create Skill</Button>
          <Button type="button" variant="default" onClick={() => { reset(); setOpen(false) }}>
            Cancel
          </Button>
        </Button.Group>
      </Stack>
    </Paper>
  )
}

function SkillList({ title, skills }: { title: string; skills: SkillProps[] }) {
  if (skills.length === 0) return null

  return (
    <div>
      <Title order={3} mb="md">{title}</Title>
      <Stack gap="sm">
        {skills.map((skill) => (
          <SkillCard key={skill.id} skill={skill} />
        ))}
      </Stack>
    </div>
  )
}

export default function SkillsIndex() {
  const { system_skills, user_skills, assigned_skills, assignments, projects, flash } = usePage<SkillIndexPageProps>().props

  return (
    <>
      <Head title="Skills" />

      <PageLayout
        title="Skills"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Skills' },
        ]}
        flash={flash}
      >
        <Stack gap="xl">
          <div>
            <Title order={3} mb="md">Create or assign skills</Title>
            <NewSkillForm />
          </div>

          <SkillList title="Your skills" skills={user_skills} />
          <SkillList title="Assigned skills" skills={assigned_skills} />
          <SkillList title="System skills" skills={system_skills} />

          {user_skills.length === 0 && assigned_skills.length === 0 && system_skills.length === 0 ? (
            <EmptyState>No skills yet. Create one to get started.</EmptyState>
          ) : null}

          <div>
            <Title order={3} mb="md">Assignments</Title>
            <Stack gap="md">
              <AssignSkillForm mode="full" allSkills={[...user_skills, ...system_skills]} projects={projects} />
              {assignments.length > 0 ? (
                <Stack gap="sm">
                  {assignments.map((assignment) => (
                    <SkillAssignmentRow key={assignment.id} assignment={assignment} showTarget />
                  ))}
                </Stack>
              ) : (
                <EmptyState>No skill assignments yet.</EmptyState>
              )}
            </Stack>
          </div>
        </Stack>
      </PageLayout>
    </>
  )
}
